import React, { useState } from 'react';
import Loader from 'components/atoms/loader';
import AiManagementSectionTitle from 'components/atoms/ai_management_section_title';
import AiManagementStatusBadge from 'components/atoms/ai_management_status_badge';
import AiManagementDataCard from 'components/molecules/ai_management_data_card';
import AiManagementCompetitorContracts from 'components/molecules/ai_management_competitor_contracts';
import AiManagementCompetitorDocuments from 'components/molecules/ai_management_competitor_documents';
import AiManagementDrawer from 'components/molecules/ai_management_drawer';
import AiManagementOcorrenciasTable from 'components/molecules/ai_management_ocorrencias_table';
import { usePossibleCompetitorDetail } from 'hooks/usePossibleCompetitorDetail';
import { PossibleCompetitorBidding } from 'services/actions/ai_management_possible_competitors';
import { count, dateBr, money, sum } from 'services/ai_management_format';
import {
  cleanObjeto,
  competitorDescription,
  finalPrice,
} from 'services/ai_management_possible_competitors';
import { Content } from './styles';

interface AiManagementCompetitorBiddingDrawerProps {
  biddingId: number | null;
  bidding: PossibleCompetitorBidding;
  itemKey: string;
  cnpj: string;
  onClose: () => void;
}

const AiManagementCompetitorBiddingDrawer: React.FC<AiManagementCompetitorBiddingDrawerProps> = ({
  biddingId,
  bidding,
  itemKey,
  cnpj,
  onClose,
}) => {
  const { detail, loading, error } = usePossibleCompetitorDetail(
    biddingId,
    bidding.bidding_id,
    itemKey,
    cnpj,
  );
  const [showInfo, setShowInfo] = useState(false);

  const licitacao = detail?.licitacao ?? bidding;
  const rows = detail?.participacoes ?? [];
  const edital = detail?.edital ?? [];
  const rejections = rows.filter((row) => row.desclassificada || row.inabilitada);

  const info = [
    {
      label: 'Nº ConLicitação',
      value: <span className="text-primary">{licitacao.bidding_id}</span>,
    },
    { label: 'Data', value: dateBr(licitacao.data) },
    { label: 'Processo', value: licitacao.processo ?? '—' },
    { label: 'Cidade', value: licitacao.cidade ?? '—' },
    { label: 'Valor estimado', value: money(licitacao.valor_estimado) },
  ];

  const prices = [
    { label: 'Inicial', value: sum(rows.map((row) => row.proposta_inicial)) },
    { label: 'Final', value: sum(rows.map(finalPrice)) },
  ];

  return (
    <AiManagementDrawer
      variant="side"
      title={licitacao.edital ?? `Licitação ${licitacao.bidding_id}`}
      subtitle={
        <>
          {licitacao.orgao ?? licitacao.cidade ?? '—'}
          <span className="mx-2">•</span>
          <button
            type="button"
            className="btn btn-link p-0 align-baseline"
            onClick={() => setShowInfo((current) => !current)}
            aria-expanded={showInfo}
          >
            Outras informações
          </button>
        </>
      }
      onClose={onClose}
    >
      <Content>
        {showInfo ? (
          <AiManagementDataCard compact className="p-4 mb-4">
            <div className="font-weight-bold">Objeto</div>
            <div className="objeto-text mb-3">{cleanObjeto(licitacao.objeto)}</div>
            <div className="row">
              {info.map((field) => (
                <div key={field.label} className="col-sm-4 mb-2">
                  <div className="font-weight-bold">{field.label}</div>
                  {field.value}
                </div>
              ))}
            </div>
          </AiManagementDataCard>
        ) : null}

        {loading ? (
          <div className="py-5">
            <Loader />
          </div>
        ) : null}

        {!loading && error ? (
          <div className="alert alert-warning mb-0" role="alert">
            Não foi possível carregar os detalhes desta licitação.
          </div>
        ) : null}

        {!loading && !error ? (
          <>
            <AiManagementSectionTitle>Detalhes do item</AiManagementSectionTitle>

            <AiManagementDataCard title="Descrição do edital" compact className="mb-3">
              {edital.length ? (
                <div className="table-responsive">
                  <table className="table bg-white mb-0">
                    <thead className="thead-light">
                      <tr className="text-nowrap">
                        <th scope="col">Item</th>
                        <th scope="col">Descrição</th>
                        <th scope="col">Qnt.</th>
                        <th scope="col">Valor unitário</th>
                        <th scope="col">Valor Estimado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {edital.map((item) => (
                        <tr key={item.numero_item}>
                          <td>{item.numero_item}</td>
                          <td
                            className="description-cell text-primary"
                            title={item.descricao ?? undefined}
                          >
                            <span>{item.descricao ?? '—'}</span>
                          </td>
                          <td className="text-nowrap">{count(item.quantidade)}</td>
                          <td className="text-nowrap font-weight-bold">
                            {money(item.valor_unitario)}
                          </td>
                          <td className="text-nowrap font-weight-bold">
                            {money(item.valor_estimado)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="px-3 py-3 text-muted">
                  Esta licitação não tem a listagem de itens do edital disponível.
                </div>
              )}
            </AiManagementDataCard>

            <AiManagementDataCard title="Descrição do concorrente" compact className="mb-4">
              <div className="table-responsive">
                <table className="table bg-white mb-0">
                  <thead className="thead-light">
                    <tr className="text-nowrap">
                      <th scope="col">Item</th>
                      <th scope="col">Descrição</th>
                      <th scope="col">Qnt.</th>
                      <th scope="col">Valor unitário</th>
                      <th scope="col">Valor total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={`${row.numero_lote}-${row.numero_item}`}>
                        <td>{row.numero_item ?? '—'}</td>
                        <td
                          className="description-cell text-primary"
                          title={competitorDescription(row)}
                        >
                          <span>{competitorDescription(row)}</span>
                        </td>
                        <td className="text-nowrap">{count(row.quantidade)}</td>
                        <td className="text-nowrap font-weight-bold">
                          {money(row.valor_unitario)}
                        </td>
                        <td className="text-nowrap font-weight-bold">{money(finalPrice(row))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </AiManagementDataCard>

            <AiManagementSectionTitle>Preço praticado</AiManagementSectionTitle>
            <div className="row mb-4">
              {prices.map((price) => (
                <div key={price.label} className="col-sm-6 mb-2">
                  <div className="price-card border p-3">
                    <div className="text-muted">
                      <i className="material-icons mr-1 text-success">receipt</i>
                      {price.label}
                    </div>
                    <div className="price-value font-weight-bold">{money(price.value)}</div>
                  </div>
                </div>
              ))}
            </div>

            <AiManagementSectionTitle>Documentos de participação</AiManagementSectionTitle>
            <AiManagementCompetitorDocuments
              biddingId={biddingId}
              historicBiddingId={bidding.bidding_id}
              cnpj={cnpj}
              className="mb-4"
            />

            <AiManagementSectionTitle>Fraquezas da empresa</AiManagementSectionTitle>
            <AiManagementDataCard title="Desclassificação / Inabilitação" compact className="mb-4">
              {rejections.length ? (
                <div className="table-responsive">
                  <table className="table bg-white mb-0">
                    <thead className="thead-light">
                      <tr className="text-nowrap">
                        <th scope="col">Tipo</th>
                        <th scope="col">Motivo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rejections.map((row) => (
                        <tr key={`${row.numero_lote}-${row.numero_item}`}>
                          <td className="text-nowrap">
                            <AiManagementStatusBadge tone="danger">
                              {row.desclassificada ? 'Desclassificada' : 'Inabilitada'}
                            </AiManagementStatusBadge>
                          </td>
                          <td>{row.motivo ?? 'Motivo não informado pelo portal.'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="px-3 py-3 text-muted">
                  Nenhuma desclassificação ou inabilitação nesta licitação.
                </div>
              )}
            </AiManagementDataCard>

            <AiManagementSectionTitle>Estratégia de Participação</AiManagementSectionTitle>
            <AiManagementOcorrenciasTable
              biddingId={biddingId}
              historicBiddingId={bidding.bidding_id}
              cnpj={cnpj}
              alwaysShow
              emptyText="Nenhuma impugnação ou esclarecimento desta empresa nesta licitação."
              theadClassName="thead-light"
              wrap={(content) => (
                <AiManagementDataCard title="Ocorrências" compact className="mb-4">
                  {content}
                </AiManagementDataCard>
              )}
            />

            <AiManagementSectionTitle>Contratos</AiManagementSectionTitle>
            <AiManagementCompetitorContracts
              biddingId={biddingId}
              historicBiddingId={bidding.bidding_id}
              cnpj={cnpj}
              className="mb-2"
            />
          </>
        ) : null}
      </Content>
    </AiManagementDrawer>
  );
};

export default AiManagementCompetitorBiddingDrawer;
